import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  useMediaQuery,
  Fab,
  Drawer,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../common/LanguageSelector";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = location.pathname === "/";
  const textColor = isHome ? "#1B365D" : "#fff";

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: isHome ? "white" : "#0C3058",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          height: "80px",
          borderRadius: "0px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            gap: "8px",
            height: "80px",
            minHeight: "80px !important",
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Logo et textes ensemble à gauche */}
          <Box
            component="button"
            tabIndex={-1}
            role="button"
            aria-label={t("header.backToHome")}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate("/");
              }
            }}
            sx={{
              display: "flex",
              gap: { xs: "8px", sm: "16px" },
              alignItems: "center",
              // justifyContent: "flex-start",
              cursor: "pointer",
              paddingLeft: { xs: "12px", sm: "0" },
              border: "none",
              background: "none",
              outline: "none",
              "&:hover": {
                opacity: 0.8,
              },
              "&:focus": {
                outline: `2px solid ${textColor}`,
                outlineOffset: "2px",
              },
              "&:focus-visible": {
                outline: `2px solid ${textColor}`,
                outlineOffset: "2px",
              },
            }}
            onClick={() => navigate("/")}
          >
            {/* Logo */}
            {!isHome && (
              <>
                <Box
                  component="img"
                  role="link"
                  tabIndex={0}
                  aria-label={t("header.ariaLabel")}
                  src={`${process.env.PUBLIC_URL}/israel.svg`}
                  alt=""
                  sx={{
                    height: { xs: 40, sm: 50 },
                    width: "auto",
                    objectFit: "contain",
                    borderRight: "2px solid #e8edf6",
                    paddingRight: { xs: 1, sm: 2 },
                  }}
                />
            
              </>
            )}

            <Box
              component="img"
              role="link"
              tabIndex={0}
              aria-label={t("header.ariaLabel")}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt=""
              sx={{
                height: { xs: 40, sm: 50 },
                width: "auto",
                objectFit: "contain",
              }}
            />

            {/* Textes multilingues directement à côté */}
            <Box
              sx={{
                textAlign: "left",
                minWidth: "fit-content",
                whiteSpace: "nowrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                  lineHeight: 1.1,
                  direction: "rtl",
                  whiteSpace: "nowrap",
                }}
              >
                {t("header.populationAuthority")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                {t("header.populationAuthorityEn")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: textColor,
                  fontWeight: 400,
                  fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                  lineHeight: 1.1,
                  direction: "rtl",
                  whiteSpace: "nowrap",
                }}
              >
                {t("header.populationAuthorityAr")}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Sélecteur de langue */}
          {!isMobile && (
            <Box sx={{ ml: 2 }}>
              <LanguageSelector textColor={textColor} />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            right: 1,
            zIndex: 3000,
            background: 'white',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LanguageSelector
            textColor="#0C3058"
            isMobile={true}
            iconOnly={true}
          />
        </Box>
      )}
      {/* Bouton flottant mobile + Drawer */}
      {/* {isMobile && (
        <>
          <Fab
            color="primary"
            aria-label={t("header.selectLanguage")}
            tabIndex={0}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 3000,
              background: "#fff",
              color: "#0C3058",
              boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
              "&:focus": {
                outline: "2px solid #0C3058",
                outlineOffset: "2px",
              },
              "&:focus-visible": {
                outline: "2px solid #0C3058",
                outlineOffset: "2px",
              },
            }}
            onClick={() => setDrawerOpen(true)}
          >
            <LanguageIcon />
          </Fab>
          <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                p: 2,
                minHeight: 120,
                background: "#fff",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 2,
              }}
            >
              <IconButton
                onClick={() => setDrawerOpen(false)}
                aria-label={t("header.closeLanguageMenu")}
                sx={{ 
                  alignSelf: "flex-end", 
                  mb: 1,
                  "&:focus": {
                    outline: "2px solid #0C3058",
                    outlineOffset: "2px",
                  },
                  "&:focus-visible": {
                    outline: "2px solid #0C3058",
                    outlineOffset: "2px",
                  },
                }}
              >
                <span style={{ fontSize: 24, fontWeight: "bold" }}>×</span>
              </IconButton>
              <LanguageSelector />
            </Box>
          </Drawer>
        </>
      )} */}
    </>
  );
};

export default Header;
